import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import { notificationsApi, AppNotification } from '../../api/notificationsApi';
import { useNotifications } from '../../contexts/NotificationsContext';
import { NotificationListItem } from './NotificationListItem';
import { NotificationOverlayHost } from './NotificationModals';
import { useNotificationActions } from '../../hooks/useNotificationActions';

export const NotificationBell: React.FC = () => {
  const { unreadCount, refreshCounts } = useNotifications();
  const { overlay, closeOverlay, openNotification, markAllRead } = useNotificationActions();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificationsApi.list({ limit: 8, offset: 0 });
      setItems(data);
      await refreshCounts();
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [refreshCounts]);

  useEffect(() => {
    if (open) {
      loadPreview();
    }
  }, [open, loadPreview]);

  useEffect(() => {
    const onChanged = () => {
      refreshCounts();
      if (open) {
        loadPreview();
      }
    };
    window.addEventListener('ebms:notifications-changed', onChanged);
    return () => window.removeEventListener('ebms:notifications-changed', onChanged);
  }, [open, loadPreview, refreshCounts]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const badgeLabel = unreadCount > 99 ? '99+' : unreadCount > 0 ? String(unreadCount) : null;

  return (
    <>
      <div className="relative" ref={panelRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="relative p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          title="Thông báo"
          aria-label="Thông báo"
        >
          <BellIcon className="w-6 h-6" />
          {badgeLabel && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[1.125rem] h-[1.125rem] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
              {badgeLabel}
            </span>
          )}
        </button>

        {open && (
          <div className="absolute right-0 mt-2 w-[min(22rem,calc(100vw-2rem))] bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
              <span className="font-semibold text-gray-900 text-sm">Thông báo</span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => markAllRead().then(() => loadPreview())}
                  className="text-xs font-medium text-blue-600 hover:text-blue-800"
                >
                  Đọc tất cả
                </button>
              )}
            </div>

            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <p className="text-sm text-gray-500 text-center py-8">Đang tải…</p>
              ) : items.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">Không có thông báo</p>
              ) : (
                items.map((n) => (
                  <NotificationListItem
                    key={n.id}
                    notification={n}
                    compact
                    onClick={() =>
                      openNotification(n, { closeDropdown: () => setOpen(false) }).then(() =>
                        loadPreview(),
                      )
                    }
                  />
                ))
              )}
            </div>

            <div className="border-t border-gray-100 px-4 py-2.5 bg-gray-50">
              <Link
                to="/notifications"
                onClick={() => setOpen(false)}
                className="block text-center text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                Xem tất cả
              </Link>
            </div>
          </div>
        )}
      </div>

      <NotificationOverlayHost overlay={overlay} onClose={closeOverlay} />
    </>
  );
};
